import React, { useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Switch,
  FormControl,
  FormLabel,
  InputLabel,
  Typography,
  FormHelperText,
  Paper,
} from '@mui/material'

const FormRenderer = ({ formSpec, onSubmit, formData = {}, onFieldChange, readOnly = false }) => {
  const [errors, setErrors] = useState({})

  // Debug logging
  React.useEffect(() => {
    console.log('[FormRenderer] Mounted/Updated:', formSpec.form_id)
    return () => {
      console.log('[FormRenderer] Unmounting:', formSpec.form_id)
    }
  })

  // Initialize form data with defaults if not already set
  const getInitialValue = (field) => {
    // Use passed formData first, then field default, then empty value
    if (formData[field.id] !== undefined) {
      return formData[field.id]
    }
    if (field.type === 'table') {
      return field.rows ? field.rows.map(r => ({ ...r })) : []
    }
    if (field.default !== undefined) {
      return field.default
    }
    if (field.type === 'checkbox' || field.type === 'multi-select') {
      return []
    }
    return ''
  }

  const handleFieldChange = (fieldId, value) => {
    // Call parent's field change handler
    if (onFieldChange) {
      onFieldChange(fieldId, value)
    }
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[fieldId]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    formSpec.fields?.forEach(field => {
      const value = formData[field.id]

      // Required validation
      if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
        newErrors[field.id] = `${field.label} is required`
        return
      }

      // Skip other validations if field is empty and not required
      if (!value) return

      // Pattern validation
      if (field.validation?.pattern) {
        const regex = new RegExp(field.validation.pattern)
        if (!regex.test(value)) {
          newErrors[field.id] = field.validation.custom_message || 'Invalid format'
        }
      }

      // Length validation
      if (typeof value === 'string') {
        if (field.validation?.min_length && value.length < field.validation.min_length) {
          newErrors[field.id] = `Minimum ${field.validation.min_length} characters required`
        }
        if (field.validation?.max_length && value.length > field.validation.max_length) {
          newErrors[field.id] = `Maximum ${field.validation.max_length} characters allowed`
        }
      }

      // Numeric validation
      if (value && (field.type === 'number' || field.type === 'integer')) {
        const numValue = typeof value === 'string' ? parseFloat(value) : value
        if (field.validation?.min !== undefined && numValue < field.validation.min) {
          newErrors[field.id] = `Minimum value is ${field.validation.min}`
        }
        if (field.validation?.max !== undefined && numValue > field.validation.max) {
          newErrors[field.id] = `Maximum value is ${field.validation.max}`
        }
      }

      // Array validation (checkbox, multi-select)
      if (Array.isArray(value)) {
        if (field.validation?.min_selected && value.length < field.validation.min_selected) {
          newErrors[field.id] = `Select at least ${field.validation.min_selected} option(s)`
        }
        if (field.validation?.max_selected && value.length > field.validation.max_selected) {
          newErrors[field.id] = `Select at most ${field.validation.max_selected} option(s)`
        }
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        form_id: formSpec.form_id,
        status: 'submitted',
        values: formData
      })
    }
  }

  const renderField = (field) => {
    const value = getInitialValue(field)
    const error = errors[field.id]

    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel':
        return (
          <TextField
            fullWidth
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
            disabled={readOnly}
            InputProps={{
              sx: { fontSize: '0.875rem' }
            }}
            InputLabelProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        )

      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
            disabled={readOnly}
            InputProps={{
              sx: { fontSize: '0.875rem' }
            }}
            InputLabelProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        )

      case 'number':
      case 'integer':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => {
              const val = field.type === 'integer'
                ? parseInt(e.target.value) || ''
                : parseFloat(e.target.value) || ''
              handleFieldChange(field.id, val)
            }}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
            disabled={readOnly}
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
              step: field.validation?.step || (field.type === 'integer' ? 1 : 0.01),
              style: { fontSize: '0.875rem' }
            }}
            InputLabelProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        )

      case 'date':
      case 'datetime':
      case 'time':
        return (
          <TextField
            fullWidth
            type={field.type === 'datetime' ? 'datetime-local' : field.type}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error || field.description}
            size="small"
            disabled={readOnly}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )

      case 'select':
        return (
          <FormControl fullWidth required={field.required} error={!!error} size="small" disabled={readOnly}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {(error || field.description) && (
              <FormHelperText>{error || field.description}</FormHelperText>
            )}
          </FormControl>
        )

      case 'radio':
        return (
          <FormControl required={field.required} error={!!error} disabled={readOnly}>
            <FormLabel>{field.label}</FormLabel>
            <RadioGroup
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            >
              {field.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {(error || field.description) && (
              <FormHelperText>{error || field.description}</FormHelperText>
            )}
          </FormControl>
        )

      case 'checkbox':
      case 'multi-select':
        return (
          <FormControl required={field.required} error={!!error} disabled={readOnly}>
            <FormLabel>{field.label}</FormLabel>
            <FormGroup>
              {field.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={value.includes?.(option.value) || false}
                      onChange={(e) => {
                        const currentValue = value || []
                        const newValue = e.target.checked
                          ? [...currentValue, option.value]
                          : currentValue.filter(v => v !== option.value)
                        handleFieldChange(field.id, newValue)
                      }}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {(error || field.description) && (
              <FormHelperText>{error || field.description}</FormHelperText>
            )}
          </FormControl>
        )

      case 'toggle':
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value || false}
                onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                disabled={readOnly}
              />
            }
            label={field.label}
          />
        )

      case 'table': {
        const rows = value || []
        const columns = field.columns || []
        return (
          <Box>
            {field.label && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {field.label}
              </Typography>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.id}
                        style={{
                          textAlign: 'left',
                          padding: '6px 10px',
                          borderBottom: '2px solid #e0e0e0',
                          whiteSpace: 'nowrap',
                          color: 'rgba(0,0,0,0.6)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      style={{ backgroundColor: rowIndex % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.025)' }}
                    >
                      {columns.map(col => (
                        <td
                          key={col.id}
                          style={{
                            padding: '6px 10px',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.875rem',
                          }}
                        >
                          {row[col.id] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )
      }

      default:
        return (
          <Typography color="error">
            Unsupported field type: {field.type}
          </Typography>
        )
    }
  }

  return (
    <Paper sx={{ p: 3, my: readOnly ? 0 : 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: readOnly ? 0 : 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} gutterBottom>
        {formSpec.title}
      </Typography>
      {formSpec.description && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {formSpec.description}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
        {formSpec.fields?.map(field => (
          <Box key={field.id}>
            {renderField(field)}
          </Box>
        ))}
      </Box>

      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
          >
            {formSpec.submit_label || 'Submit'}
          </Button>
        </Box>
      )}
    </Paper>
  )
}

// Wrap in React.memo to prevent re-renders when parent re-renders
export default React.memo(FormRenderer, (prevProps, nextProps) => {
  // Only re-render if formSpec, onSubmit, onFieldChange, readOnly, or formData references change
  return (
    prevProps.formSpec === nextProps.formSpec &&
    prevProps.onSubmit === nextProps.onSubmit &&
    prevProps.onFieldChange === nextProps.onFieldChange &&
    prevProps.formData === nextProps.formData &&
    prevProps.readOnly === nextProps.readOnly
  )
})
